import { useEffect, useState } from 'react';

import FormControl from '@zenbusiness/zui/FormControl';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { ADDRESS } from '#client/constants/application';
import useCardAddress from '#client/hooks/useCardAddress';
import useOrderCard from '#client/hooks/useOrderCard';
import useToastContext from '#client/hooks/useToastContext';

import CustomizedDialog from './components/CustomizedDialog';
import { FormControlLabelStyled } from './components/FormControlLabel.style';
import TitleAndDescription from './components/TitleAndDescription';

export default function OpenDebitCardMailingV2({ handleClose }) {
  const [value, setValue] = useState(null);
  const { businessAddress, personalAddress, loading: addressLoading, error } = useCardAddress();
  const { handleSubmitAddress, loading: submitLoading } = useOrderCard();
  const { createErrorToast } = useToastContext();

  useEffect(() => {
    if (error) {
      createErrorToast({
        title: 'We need your address',
        description:
          'To ship your new banking card, we still need your mailing address. Please contact customer support with this info.',
        error
      });
    }
  }, [createErrorToast, error]);

  return (
    <CustomizedDialog
      buttonProps={{
        disabled: !value,
        loading: submitLoading,
        onClick: async () => {
          if (value === ADDRESS.BUSINESS) {
            await handleSubmitAddress(businessAddress);
          } else {
            await handleSubmitAddress(personalAddress);
          }
          handleClose();
        },
        text: 'Submit'
      }}
      handleClose={handleClose}
      title="Debit Card"
    >
      <Stack
        gap={{ xs: '24px', md: '40px' }}
        my={{ xs: '24px', md: '40px' }}
        width={{ xs: '100%', md: 530 }}
      >
        <TitleAndDescription />
        <FormControl disabled={addressLoading || submitLoading}>
          <RadioGroup
            aria-labelledby="radio-group-address"
            onChange={(event) => setValue(event.target.value)}
            value={value}
          >
            <FormControlLabelStyled
              control={<Radio color="info" />}
              label={
                <Text
                  color="primary"
                  size="lg"
                  weight="medium"
                >
                  {businessAddress.street ?? <Skeleton width={200} />}
                </Text>
              }
              selected={value === ADDRESS.BUSINESS}
              sx={{ mb: '8px' }}
              value={ADDRESS.BUSINESS}
            />
            <FormControlLabelStyled
              control={<Radio color="info" />}
              label={
                <Text
                  color="primary"
                  size="lg"
                  weight="medium"
                >
                  {personalAddress.street ?? <Skeleton width={200} />}
                </Text>
              }
              selected={value === ADDRESS.PERSONAL}
              value={ADDRESS.PERSONAL}
            />
          </RadioGroup>
        </FormControl>
      </Stack>
    </CustomizedDialog>
  );
}

OpenDebitCardMailingV2.propTypes = {
  handleClose: PropTypes.func
};
